import React, { useMemo, useState } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useNavigate } from "react-router-dom";
import { TableAction, TableColumn } from "@shared/types/table";
import { useSingleBillList } from "@features/bx/singleBillGenaration/hooks";
import { PageHeader } from "@shared/components/PageHeader";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { useForm } from "react-hook-form";
import { SingleBillFilter } from "./SingleBillFilter";

// Types based on data structure
interface BillCycle {
  id: string;
  billId?: string | number;
  draftId?: string | number;
  billNumber?: string;
  consumerName?: string;
  consumerNo?: string;
  billDate?: string;
  dueDate?: string;
  amount?: number | string;
  billStatus?: string;
  step?: {
    stepWorkingOn?: number;
  };
  name?: string;
  consumerCount?: number;
  premiseCount?: number;
  lastRunDate?: string;
  days?: number;
  runCount?: number;
  createdBy?: string;
  status?: "Active" | "Inactive";
}

interface SingleBillFilters {
  page?: number;
  limit?: number;
  status?: string;
  search_data?: string;
}

interface SingleBillListProps {
  data?: any; // API response data
  currentPage?: number;
  onPageChange?: (page: number) => void;
}

export default function SingleBillList({
  data: propData,
  currentPage: initialPage = 1,
  onPageChange: propOnPageChange,
}: SingleBillListProps) {
  const currency = useCurrency();
  const navigate = useNavigate();
  const [page, setPage] = useState(initialPage);
  localStorage.removeItem("single-bill");

  const [filters, setFilters] = useState<SingleBillFilters>({
    page: initialPage,
    limit: PAGE_SIZE,
    status: "",
    search_data: "",
  });

  const form = useForm({
    defaultValues: {
      status: "",
    },
  });

  const { data: billCycleList, isLoading: apiLoading } =
    useSingleBillList(filters);

  const isLoading = propData ? false : apiLoading;
  const tableData = useMemo(() => {
    if (propData) {
      return propData.result || propData.results || propData || [];
    }
    return billCycleList?.result || billCycleList?.results || [];
  }, [propData, billCycleList]);

  const totalCount = useMemo(() => {
    if (propData) {
      return propData.count ?? tableData.length;
    }
    return billCycleList?.count || 0;
  }, [propData, billCycleList, tableData.length]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  const pagination = useMemo(
    () => ({
      currentPage: page,
      totalPages,
      totalItems: totalCount,
      itemsPerPage: PAGE_SIZE,
      startItem: (page - 1) * PAGE_SIZE + 1,
      endItem: Math.min(page * PAGE_SIZE, totalCount),
    }),
    [page, totalPages, totalCount],
  );

  const handleApplyFilters = (newFilters: Partial<SingleBillFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: 1,
    }));
    setPage(1);
    propOnPageChange?.(1);
  };

  const handleResetFilters = () => {
    setFilters({
      page: 1,
      limit: PAGE_SIZE,
      status: "",
      search_data: "",
    });
    setPage(1);
    form.reset();
    propOnPageChange?.(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilters((prev) => ({
      ...prev,
      page: newPage,
    }));
    propOnPageChange?.(newPage);
  };

  const handleSearch = (searchTerm: string) => {
    setFilters((prev) => ({
      ...prev,
      search_data: searchTerm,
      page: 1,
    }));
    setPage(1);
    propOnPageChange?.(1);
  };

  // Handlers
  const handleViewBill = (row: any) => {
    navigate(`/bx/bill-detail/${row.billId}`);
  };

  const handleEditBill = (row: any) => {
    const stepMap = [
      "",
      "consumer-setup",
      "account-overview",
      "readings-payments",
      "generate-bill",
    ];
    const stepNum = row.step?.stepWorkingOn || 1;
    const stepSlug = stepMap[stepNum] || "consumer-setup";

    navigate(
      `/bx/single-bill-generation/?action=edit&draftId=${row.draftId}#${stepSlug}`,
    );
  };

  const columns: TableColumn<any>[] = useMemo(
    () => [
      {
        key: "billNumber",
        header: "Bill Number",
      },
      {
        key: "consumerName",
        header: "Consumer",
      },
      {
        key: "consumerNo",
        header: "Account No",
      },
      {
        key: "billDate",
        header: "Date",
        render: (value: string) => <div>{value || "NA"}</div>,
      },
      {
        key: "dueDate",
        header: "Due Date",
      },
      {
        key: "amount",
        header: "Amount",
        render: (value: any) => (
          <div>
            {currency} {value}
          </div>
        ),
      },
      {
        key: "billStatus",
        header: "Status",
        type: "badge",
      },
    ],
    [currency],
  );

  const tableActions: TableAction[] = useMemo(
    () => [
      {
        label: "View",
        onClick: (row) => {
          handleViewBill(row);
        },
        hidden: (row) => ["IN_PROGRESS", "PENDING"].includes(row.billStatus),
      },
      {
        label: "Edit",
        onClick: (row) => {
          handleEditBill(row);
        },
        hidden: (row) => !["IN_PROGRESS", "PENDING"].includes(row.billStatus),
      },
    ],
    [],
  );

  return (
    <div className="">
      <PageHeader
        title="Single Bill List"
        subtitle="View and manage individually generated bills."
        buttonName="Create New Bill"
        handleSubmit={() => navigate(`/bx/single-bill-generation`)}
      />

      <div className="mt-6">
        <GenericTable
          data={tableData}
          columns={columns}
          rowKey="id"
          actions={tableActions}
          actionsType="dropdown"
          emptyMessage="No bill cycles found"
          loading={isLoading}
          onSearch={handleSearch}
          searchPlaceholder="Search by bill number, account no, consumer..."
          pagination={pagination}
          onPageChange={handlePageChange}
          filterPanel={
            <SingleBillFilter
              form={form}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            />
          }
          getRowClassName={(row: BillCycle) => "cursor-pointer hover:bg-gray-50"}
        />
      </div>
    </div>
  );
}
